/**
 * Translate a ServiceNow encoded query (e.g. `priorityIN1,2^state=2^EQ`) into
 * a plain-English clause list.
 *
 * Rules:
 *   `^EQ`            — terminator, dropped
 *   `^OR`            — splits OR clauses *within* an AND group
 *   `^NQ`            — starts a new AND group (joined with " or " between groups)
 *   `^`              — joins AND clauses
 *
 * Operators handled: `=`, `!=`, `<=`, `>=`, `IN`, `NOT IN`, `LIKE`,
 *   `STARTSWITH`, `ENDSWITH`, `ISEMPTY`, `ISNOTEMPTY`, `BETWEEN`.
 *
 * Returns `null` when nothing parseable remains — callers should hide the
 * cell in that case rather than show garbage.
 */
export function humanizeCondition(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let s = raw.trim();
  if (!s) return null;
  // ServiceNow sometimes ships SLA conditions as the *display* string
  // ("Priority = 3 - Moderate .and. State = Work In Progress") rather
  // than an encoded query. Clean the literal `.and.` / `.or.` separators.
  if (!s.includes('^') && /\.(and|or)\./i.test(s)) {
    return s
      .replace(/\s*\.and\.\s*/gi, ' and ')
      .replace(/\s*\.or\.\s*/gi, ' or ')
      .trim();
  }
  // Strip the terminator. Some payloads ship multiple.
  s = s.replace(/\^EQ$/g, '').replace(/\^EQ/g, '');
  if (!s) return null;

  // Split into OR-of-AND groups by `^NQ` (new query). Inside each group,
  // clauses are joined with `^` (AND) or `^OR` (OR).
  const orGroups = s.split('^NQ').map((g) => g.trim()).filter(Boolean);
  const renderedGroups = orGroups
    .map(renderAndGroup)
    .filter((g): g is string => g != null);

  if (renderedGroups.length === 0) return null;
  if (renderedGroups.length === 1) return renderedGroups[0];
  return renderedGroups.map((g) => `(${g})`).join(' OR ');
}

function renderAndGroup(group: string): string | null {
  // Tokens are clauses separated by `^`; the literal `^OR` token is a
  // disjunction with the previous clause.
  const tokens = group.split('^').filter((t) => t !== '');
  const clauses: { text: string; isOr: boolean }[] = [];
  for (const tok of tokens) {
    const isOr = tok.startsWith('OR');
    const body = isOr ? tok.slice(2) : tok;
    const rendered = renderClause(body);
    if (rendered) clauses.push({ text: rendered, isOr: isOr && clauses.length > 0 });
  }
  if (clauses.length === 0) return null;
  // Render: chain together with `AND` / `OR` per the isOr flag.
  return clauses
    .map((c, i) => (i === 0 ? c.text : (c.isOr ? ` OR ${c.text}` : ` AND ${c.text}`)))
    .join('');
}

const FIELD_LABELS: Record<string, string> = {
  priority: 'Priority',
  state: 'State',
  active: 'Active',
  sys_mod_count: 'Update count',
  assignment_group: 'Assignment group',
  assigned_to: 'Assigned to',
  approval: 'Approval',
  item: 'Catalog item',
  cat_item: 'Catalog item',
  short_description: 'Short description',
  category: 'Category',
};

// ServiceNow's standard ITSM/ITSM-derived state codes for sc_req_item /
// sc_request — we only label widely-shared values. Anything table-specific
// stays as the raw number; better numeric than wrong-labelled.
const STATE_LABELS: Record<string, string> = {
  '-5': 'Pending',
  '1': 'Open',
  '2': 'Work in Progress',
  '3': 'Closed Complete',
  '4': 'Closed Incomplete',
  '5': 'Closed Skipped',
  '6': 'Pending',
  '7': 'Cancelled',
};

const PRIORITY_LABELS: Record<string, string> = {
  '1': 'Critical',
  '2': 'High',
  '3': 'Moderate',
  '4': 'Low',
  '5': 'Planning',
};

// Order matters: longer ops first so `<=` is tried before `=`.
const OPERATORS: { op: string; render: string }[] = [
  { op: 'ISNOTEMPTY', render: 'is not empty' },
  { op: 'ISEMPTY', render: 'is empty' },
  { op: 'STARTSWITH', render: 'starts with' },
  { op: 'ENDSWITH', render: 'ends with' },
  { op: 'NOT IN', render: 'not in' },
  { op: 'NOTIN', render: 'not in' },
  { op: 'BETWEEN', render: 'between' },
  { op: 'LIKE', render: 'contains' },
  { op: 'IN', render: 'in' },
  { op: '!=', render: '≠' },
  { op: '<=', render: '≤' },
  { op: '>=', render: '≥' },
  { op: '=', render: '=' },
  { op: '<', render: '<' },
  { op: '>', render: '>' },
];

function renderClause(body: string): string | null {
  if (!body) return null;
  for (const { op, render } of OPERATORS) {
    const idx = body.indexOf(op);
    if (idx <= 0) continue;
    const field = body.slice(0, idx);
    const value = body.slice(idx + op.length);
    return formatClause(field, render, value);
  }
  return null;
}

function formatClause(rawField: string, op: string, rawValue: string): string {
  const fieldLabel = FIELD_LABELS[rawField] ?? prettifyField(rawField);
  if (op === 'is empty' || op === 'is not empty') {
    return `${fieldLabel} ${op}`;
  }
  const value = formatValue(rawField, rawValue, op);
  return `${fieldLabel} ${op} ${value}`;
}

function formatValue(field: string, raw: string, op: string): string {
  if (op === 'in' || op === 'not in') {
    const parts = raw.split(',').map((p) => labelForValue(field, p.trim()));
    return parts.length > 1 ? `(${parts.join(', ')})` : parts[0] || '';
  }
  if (op === 'between') {
    const [a = '', b = ''] = raw.split('@');
    return `${labelForValue(field, a)} and ${labelForValue(field, b)}`;
  }
  return labelForValue(field, raw);
}

function labelForValue(field: string, raw: string): string {
  const v = raw.trim();
  if (!v) return '""';
  if (field === 'state' && STATE_LABELS[v]) return STATE_LABELS[v];
  if (field === 'priority' && PRIORITY_LABELS[v]) return PRIORITY_LABELS[v];
  // 32-hex sys_ids → hide them (we don't want sys_ids in the UI).
  if (/^[0-9a-f]{32}$/i.test(v)) return '<a record>';
  return v;
}

function prettifyField(raw: string): string {
  // ServiceNow column names are snake_case (often u_-prefixed). Render as
  // sentence case, drop the prefix.
  const stripped = raw.replace(/^u_/, '');
  return stripped
    .split('_')
    .filter(Boolean)
    .map((w, i) => (i === 0 ? capitalize(w) : w))
    .join(' ');
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s[0].toUpperCase() + s.slice(1);
}
